import Image from "next/image";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Inbox, Loader2 } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import axios from "axios";
import Link from "next/link";
import { NextSeo } from "next-seo";

const formSchema = z.object({
  firstName: z.string().min(2, "First name must be at least 2 characters"),
  lastName: z.string().min(2, "Last name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
});

type FormData = z.infer<typeof formSchema>;

const NewsletterPage = () => {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
    },
  });

  const { isSubmitting } = form.formState;

  const onSubmit = async (data: FormData) => {
    setStatus("idle");
    setMessage("");

    try {
      await axios.post("/api/subscribe", data);
      setStatus("success");
      setMessage(
        "Thanks for subscribing! You'll receive a personal email from me to say hi."
      );
      form.reset();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      setStatus("error");
      setMessage(error.response.data.error);
    }
  };

  return (
    <>
      <NextSeo
        title="Newsletter | Web Performance & Engineering"
        description="Subscribe to my newsletter for everything related to web performance and engineering. No spam, just the good stuff with great vibes."
        canonical="https://amrik.live/newsletter"
        openGraph={{
          url: "https://amrik.live/newsletter",
          title: "Newsletter | Web Performance & Engineering",
          description:
            "Subscribe to my newsletter for everything related to web performance and engineering. No spam, just the good stuff with great vibes.",
          images: [
            {
              url: "https://uoubfkdsc2yugclb.public.blob.vercel-storage.com/banner.jpeg",
              width: 1200,
              height: 630,
              alt: "Newsletter - Web Performance & Engineering",
            },
          ],
          siteName: "Amrik Malhans - Web Performance & Engineering",
        }}
      />
      <div className="relative h-screen overflow-hidden bg-zinc-900">
        <Image
          src="/images/banner.webp"
          alt="Newsletter background"
          fill
          loading="eager"
          priority
          className="object-cover opacity-50"
        />
        <div className="relative z-10 flex flex-col items-center justify-center h-full">
          <div className="flex flex-col items-center justify-center space-y-4 sm:space-y-6 text-center px-4 py-8 max-w-lg w-full mx-4">
            <div>
              <Image
                src="/images/amrik-malhans-1.png"
                alt="avatar"
                width={56}
                height={56}
                loading="eager"
                priority
                className="rounded-xl border-[1.4px] border-zinc-200"
              />
            </div>

            <h2 className="text-2xl font-bold tracking-wide text-zinc-100">
              Web Performance & Engineering
            </h2>

            <p className="text-sm md:text-base text-zinc-300">
              Subscribe to my newsletter for everything related to web performance
              and engineering. No spam, just the good stuff with great vibes.
            </p>

          {status === "success" ? (
            <div
              className="text-green-400
             flex items-center gap-2 animate-in fade-in"
            >
              ✨ {message}
            </div>
          ) : (
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="flex flex-col w-full gap-3"
              >
                <div className="flex flex-col sm:flex-row gap-2">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormControl>
                          <Input
                            placeholder="First name"
                            disabled={isSubmitting}
                            className="bg-zinc-300"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormControl>
                          <Input
                            placeholder="Last name"
                            disabled={isSubmitting}
                            className="bg-zinc-300"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="name@email.com"
                          disabled={isSubmitting}
                          className="bg-zinc-300"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Subscribing...
                    </>
                  ) : (
                    <>
                      Subscribe
                      <Inbox className="h-4 w-4" />
                    </>
                  )}
                </Button>

                {status === "error" && (
                  <p className="text-red-400 text-sm mt-1 animate-in fade-in">
                    {message}
                  </p>
                )}
              </form>
            </Form>
          )}
          </div>

          <div className="flex text-sm tracking-wide gap-4 text-zinc-200">
            <Link
              href="/"
              className="hover:text-white transition-colors duration-200"
            >
              Home
            </Link>
            <Link
              href="/blog"
              className="hover:text-white transition-colors duration-200"
            >
              Blog
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default NewsletterPage;
